/**
 * Bounding boxes and transforms for annotation elements (canvas natural coords).
 */

const MEASURE_CTX =
  typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;

export function measureTextBounds(ctx, el) {
  const c = ctx || MEASURE_CTX;
  if (!c) return { x: el.x, y: el.y, w: 80, h: 24 };
  const fontSize = el.fontSize || 16;
  c.save();
  c.font = `${fontSize}px ui-sans-serif, system-ui, -apple-system, sans-serif`;
  const raw = el.text ?? '';
  const lines = raw.length ? raw.split('\n') : [''];
  let maxW = fontSize * 4;
  for (const line of lines) {
    const t = line.length ? line : ' ';
    maxW = Math.max(maxW, c.measureText(t).width);
  }
  c.restore();
  const lineH = fontSize * 1.25;
  const h = Math.max(lineH, lines.length * lineH);
  return { x: el.x, y: el.y, w: maxW + 12, h: h + 6 };
}

/**
 * Axis-aligned bounds used for selection, handles, and resize mapping.
 * @param {object} el
 * @param {CanvasRenderingContext2D | null} [ctx]
 */
export function getElementBounds(el, ctx) {
  switch (el.type) {
    case 'rect':
    case 'blur':
      return { x: el.x, y: el.y, w: el.w, h: el.h };
    case 'arrow': {
      const minX = Math.min(el.x1, el.x2);
      const minY = Math.min(el.y1, el.y2);
      const maxX = Math.max(el.x1, el.x2);
      const maxY = Math.max(el.y1, el.y2);
      const pad = (el.strokeWidth || 2) + 10;
      return {
        x: minX - pad,
        y: minY - pad,
        w: Math.max(8, maxX - minX + 2 * pad),
        h: Math.max(8, maxY - minY + 2 * pad),
      };
    }
    case 'path': {
      if (!el.points || el.points.length < 1) {
        return { x: 0, y: 0, w: 8, h: 8 };
      }
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of el.points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }
      const pad = (el.strokeWidth || 2) + 10;
      return {
        x: minX - pad,
        y: minY - pad,
        w: Math.max(8, maxX - minX + 2 * pad),
        h: Math.max(8, maxY - minY + 2 * pad),
      };
    }
    case 'text':
      return measureTextBounds(ctx, el);
    default:
      return { x: 0, y: 0, w: 0, h: 0 };
  }
}

/**
 * Map element geometry into a new bounding box (from uniform resize drag).
 * @param {object} el frozen snapshot at gesture start
 * @param {{ x: number; y: number; w: number; h: number }} oldB
 * @param {{ x: number; y: number; w: number; h: number }} newB
 */
export function transformElementToBounds(el, oldB, newB) {
  const ow = Math.max(1, oldB.w);
  const oh = Math.max(1, oldB.h);
  if (el.type === 'rect' || el.type === 'blur') {
    return { ...el, x: newB.x, y: newB.y, w: newB.w, h: newB.h };
  }
  if (el.type === 'arrow') {
    const map = (px, py) => ({
      x: newB.x + ((px - oldB.x) / ow) * newB.w,
      y: newB.y + ((py - oldB.y) / oh) * newB.h,
    });
    const p1 = map(el.x1, el.y1);
    const p2 = map(el.x2, el.y2);
    return { ...el, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
  }
  if (el.type === 'path') {
    return {
      ...el,
      points: el.points.map((p) => ({
        x: newB.x + ((p.x - oldB.x) / ow) * newB.w,
        y: newB.y + ((p.y - oldB.y) / oh) * newB.h,
      })),
    };
  }
  if (el.type === 'text') {
    const sx = newB.w / ow;
    const sy = newB.h / oh;
    const s = Math.min(sx, sy);
    return {
      ...el,
      x: newB.x,
      y: newB.y,
      fontSize: Math.max(10, Math.round((el.fontSize || 16) * s)),
    };
  }
  return el;
}
